import React, {useCallback, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {createUUId} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import {DialogActions, DialogContent, DialogTitle, MenuItem, Popper} from "@mui/material";
import EnvService from "../../../../core/services/env";
import Form, {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import Api from "../../../../core/services/api/api";
import useAccessLevels from "../../../hooks/use-access-levels";
import {styled} from "@mui/material/styles";


const schema = Yup.object().shape({
    country: Yup.mixed().nullable(),
    province: Yup.mixed().nullable(),
    city: Yup.string().trim().nullable(),
    trackingNo: Yup.string().trim().nullable(),
    eta: Yup.mixed().validDate().nullable(),
    shippingCompany: Yup.mixed().nullable(),
    note: Yup.string().trim().nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const PickersPopperRoot = styled(Popper, {
    name: 'MuiPickersPopper',
    slot: 'Root',
    overridesResolver: (_, styles) => styles.root,
})(({theme}) => ({
    zIndex: theme.zIndex.modal + 100,
}));

const EditPartPurchaseEtaDialog = ({
                                       open,
                                       close,
                                       record,
                                       onUpdate,
                                       shippingCompanies,
                                       countries,
                                   }) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManagePurchaseList = packages.lookup.manageProductOnPurchaseList;
    const formIdRef = useRef(createUUId(true));

    const initialValues = useMemo(() => {
        const provinceCode = record?.shipmentProvinceCode;
        let province = null;
        let country = null;
        if (!!provinceCode) {
            for (const c of countries) {
                const foundProvince = c.provinces?.find(p => p.code === provinceCode);
                if (!!foundProvince) {
                    province = foundProvince;
                    country = c;
                    break;
                }
            }
        }
        return ({
            country: country,
            province: province,
            city: record?.shipmentCity ?? '',
            trackingNo: record?.trackingNumber ?? '',
            eta: record?.eta ? DateUtils.serverToUTC(record?.eta) : null,
            shippingCompany: shippingCompanies?.find(e => e.code === record?.shipViaCode) ?? null,
            note: record?.note ?? '',
        });
    }, [record, countries, shippingCompanies])


    /**
     * Creates / Edits the address by invoking the [address] callback.
     * @param values {any} the form values.
     */
    const onSubmit = useCallback(async (values) => {
        if (!canManagePurchaseList) return;

        const information = {
            purchaseId: record.purchaseId,
            trackingNumber: values.trackingNo,
            eta: values.eta ? DateUtils.UTCToServer(values.eta) : null,
            shipViaCode: values?.shippingCompany?.code,
            shipmentProvinceCode: values?.province?.code,
            shipmentCity: values?.city,
            note: values?.note ?? '',
        }
        setSubmitting(true);
        const response = await Api.updatePurchaseETAInfo(information);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onUpdate(information);
            close();
        }
        if (!isMounted())
            return;
        setSubmitting(false);
    }, [canManagePurchaseList, record, onUpdate, close, isMounted]);

    return (
        <Dialog
            open={open}
            setOpen={close}
            loading={submitting}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            className={'draggable-popover-cancel'}
            sx={{zIndex: (theme) => theme.zIndex.modal + 2}}
        >
            <DialogTitle id={'scroll-dialog-title'} className={'py-0'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Edit Purchase Information
                </p>
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={onSubmit}
                    formProps={{id: formIdRef.current, className: 'w-100'}}
                >
                    {({values, form}) => {
                        const provinces = countries.find(e => values.country?.id === e.id)?.provinces ?? [];
                        const colClass = 'px-3 mb-4';
                        return (
                            <>
                                <Row className='no-gutters w-100'>
                                    <Col xs={12} className={colClass}>
                                        <MuiRFF.TextField
                                            fullWidth
                                            label={"Tracking Number"}
                                            name={'trackingNo'}
                                            required={required.trackingNo}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <MuiRFF.DatePicker
                                            fullWidth
                                            label={'ETA'}
                                            name={'eta'}
                                            required={required.eta}
                                            slots={{popper: PickersPopperRoot}}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <MuiRFF.TextField
                                            select
                                            label={"Ship Via"}
                                            name={'shippingCompany'}
                                            required={required.shippingCompany}
                                            SelectProps={{
                                                renderValue: (value) => value?.title,
                                                MenuProps: {
                                                    sx: {zIndex: (theme) => theme.zIndex.modal + 100},
                                                }
                                            }}
                                        >
                                            {
                                                shippingCompanies?.map(e => (
                                                    <MenuItem key={e?.id} value={e}>
                                                        {e?.title ?? ""}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Col>
                                    <Col xs={6} className={colClass}>
                                        <MuiRFF.TextField
                                            select
                                            label={"Ship to Country"}
                                            name={'country'}
                                            required={required.country}
                                            SelectProps={{
                                                onChange: (e) => {
                                                    form.change('province', null);
                                                    form.change('country', e.target.value);
                                                },
                                                renderValue: (value) => value?.name,
                                                MenuProps: {
                                                    sx: {zIndex: (theme) => theme.zIndex.modal + 100},
                                                }
                                            }}
                                        >
                                            {
                                                countries.map(e => (
                                                    <MenuItem key={e.id} value={e}>
                                                        {e.name}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Col>
                                    <Col xs={6} className={colClass}>
                                        <MuiRFF.TextField
                                            select
                                            label={"Ship to " + (!values.country ? 'Province / State' : values.country?.name === EnvService.USATitle ? "State" : 'Province')}
                                            name={'province'}
                                            required={required.province}
                                            SelectProps={{
                                                renderValue: (value) => value?.name,
                                                MenuProps: {
                                                    sx: {zIndex: (theme) => theme.zIndex.modal + 100},
                                                }
                                            }}
                                        >
                                            {
                                                provinces.map(e => (
                                                    <MenuItem key={e.id} value={e}>
                                                        {e.name}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <MuiRFF.TextField
                                            fullWidth
                                            label={"Ship to City"}
                                            name={'city'}
                                            required={required.city}
                                            InputProps={{
                                                placeholder: "Enter city name",
                                            }}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <MuiRFF.TextField
                                            fullWidth
                                            label={"Note"}
                                            name={'note'}
                                            multiline
                                            minRows={4}
                                            required={required.note}
                                        />
                                    </Col>
                                </Row>
                            </>
                        );
                    }}
                </Form>
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    onClick={() => close()}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={formIdRef.current}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        submitting
                            ? 'Saving...'
                            : "Save Changes"
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default EditPartPurchaseEtaDialog;
